import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Button from "components/button";
import UseContext from "hooks/useContext";
import ApiDatabase from "server";
import useTitle from "../../hooks/TitleManager";

const VIEW_STATUS = {
  LOADING: "loading",
  DOCUMENT: "document",
  NOT_FOUND: "notFound",
  ERROR: "error",
};

const PdfView = () => {
  useTitle({ title: "Ouverture fichier" });
  const { language } = UseContext();

  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const fileName = searchParams.get("fileName") ?? "";
  const tokenFromQuery = searchParams.get("token") ?? "";
  const [docs, setDocs] = useState([]);
  const [status, setStatus] = useState(
    type ? VIEW_STATUS.LOADING : VIEW_STATUS.NOT_FOUND
  );
  // Ignores a late response from a previous call once "Réessayer" started a new one.
  const latestRequestId = useRef(0);

  const manageFiles = useCallback(() => {
    if (!type) {
      setDocs([]);
      setStatus(VIEW_STATUS.NOT_FOUND);
      return;
    }

    const requestId = ++latestRequestId.current;
    setStatus(VIEW_STATUS.LOADING);

    ApiDatabase.postPresignFiles(
      {
        type,
        fileName,
        ...(tokenFromQuery ? { token: tokenFromQuery } : {}),
      },
      (data) => {
        if (requestId !== latestRequestId.current) return;
        const urls = Array.isArray(data?.urls) ? data.urls : [];
        const usableUrls = urls.filter((item) => item && (item.url || item.uri));
        const docsFromLinks = usableUrls.map((item) => {
          const href = item.url || item.uri;
          let originalFileName = "";
          try {
            const u = new URL(href);
            originalFileName = decodeURIComponent(
              (u.pathname.split("/").pop() || "").trim()
            );
          } catch {
            const clean = href.split("?")[0].split("#")[0];
            originalFileName = decodeURIComponent(
              (clean.split("/").pop() || "").trim()
            );
          }
          return {
            uri: href,
            fileName: originalFileName || "document",
            fileType: item.type || "application/pdf",
          };
        });
        setDocs(docsFromLinks);
        setStatus(
          docsFromLinks.length > 0
            ? VIEW_STATUS.DOCUMENT
            : VIEW_STATUS.NOT_FOUND
        );
      },
      (err) => {
        if (requestId !== latestRequestId.current) return;
        console.error(err);
        setDocs([]);
        setStatus(VIEW_STATUS.ERROR);
      }
    );
  }, [type, fileName, tokenFromQuery]);

  useEffect(() => {
    manageFiles();
  }, [manageFiles]);

  return (
    <div className="flex h-screen w-full min-h-0 flex-col overflow-hidden">
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {status === VIEW_STATUS.LOADING ? (
          <div className="flex h-full flex-col space-y-6 border-b p-6 shadow md:flex-row md:space-x-4 md:space-y-0">
            <div className="h-20 w-20 shrink-0 animate-pulse rounded bg-gray-200" />
            <div className="flex-1 space-y-3">
              <div className="h-5 w-1/3 animate-pulse rounded bg-gray-200" />
              <div className="h-4 w-1/4 animate-pulse rounded bg-gray-200" />
              <div className="h-3 w-2/3 animate-pulse rounded bg-gray-200" />
              <div className="h-3 w-3/4 animate-pulse rounded bg-gray-200" />
            </div>
          </div>
        ) : status === VIEW_STATUS.DOCUMENT ? (
          <div className="relative h-full min-h-0 w-full">
            <DocViewer
              style={{
                position: "absolute",
                inset: 0,
                minHeight: 0,
                width: "100%",
                height: "100%",
              }}
              documents={docs}
              config={{
                pdfZoom: {
                  defaultZoom: 1,
                  zoomJump: 0.1,
                },
              }}
              pluginRenderers={DocViewerRenderers}
            />
          </div>
        ) : status === VIEW_STATUS.ERROR ? (
          <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-y-auto px-4 text-center text-gray-500">
            <p>
              {language.getValue(
                "pdf_view.error",
                "Le document n'a pas pu être chargé. Réessayez dans quelques instants."
              )}
            </p>
            <Button color={"PRIMARY"} size={"SM"} onClick={manageFiles}>
              {language.getValue("pdf_view.retry", "Réessayer")}
            </Button>
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-4 text-center text-gray-500">
            <p>
              {language.getValue(
                "pdf_view.not_found",
                "Ce document est introuvable. Il n'a peut-être pas été envoyé, ou a été supprimé."
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfView;
